/**
 * MirrorVerificationPage
 * 
 * Página de verificação e match fiscal de espelhos de nota.
 * Compara dados extraídos do PDF/XML/Excel com os dados da requisição original.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, Upload, Sparkles,
  CheckCircle2, AlertCircle, Loader2,
  ArrowRight, Info, FileCheck,
  ScanSearch, BrainCircuit, RefreshCcw,
  CreditCard, Building2, X, Calendar, DollarSign, XCircle
} from 'lucide-react';
import { mirrorService, financeService, purchaseService } from '../../../services/purchasesApi';
import { supplierService } from '../../../services/erpApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import ItemConnector from '../../../components/ERP/Compras/ItemConnector';
import './MirrorVerificationPage.css';

const MirrorVerificationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast, showToast, hideToast } = useToast();
  
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [totalWithTaxes, setTotalWithTaxes] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [matchResults, setMatchResults] = useState(null); // Armazenar resultados do matching
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  
  // Refs para os cards (para conectores visuais)
  const originalItemRefs = useRef({});
  const extractedItemRefs = useRef({});
  const splitContainerRef = useRef(null);
  const leftPanelContentRef = useRef(null); // Ref para o painel de conteúdo da esquerda
  const rightPanelContentRef = useRef(null); // Ref para o painel de conteúdo da direita
  const scrollIntervalRef = useRef(null); // Ref para o intervalo de scroll
  const [itemOrder, setItemOrder] = useState({ original: [], extracted: [] }); // Ordenação customizada
  
  // Estados para drag and drop de conexão manual
  const [draggingItem, setDraggingItem] = useState(null); // { type: 'original' | 'extracted', index: number, id: number }
  const [hoveringItem, setHoveringItem] = useState(null); // { type: 'original' | 'extracted', index: number, id: number }
  const [manualConnections, setManualConnections] = useState({}); // { [extractedIdx]: originalItemId }
  
  // Estados do modal de forma de pagamento
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [installments, setInstallments] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState('');
  const [intervalDays, setIntervalDays] = useState(30);
  const [calculating, setCalculating] = useState(false);
  const [installmentsPreview, setInstallmentsPreview] = useState(null);
  const [paymentErrors, setPaymentErrors] = useState({});
  
  // Estados do modal de fornecedor
  const [supplierData, setSupplierData] = useState({ name: '', cnpj: '' });
  const [searchingSupplier, setSearchingSupplier] = useState(false);
  const [foundSupplier, setFoundSupplier] = useState(null);

  // Inicializar dados do modal de pagamento quando abrir
  useEffect(() => {
    if (showPaymentModal && requisition) {
      if (requisition.payment_method) {
        setPaymentMethod(requisition.payment_method);
        setInstallments(requisition.payment_installments || 1);
        setFirstDueDate(requisition.payment_first_due_date || '');
        setIntervalDays(requisition.payment_interval_days || 30);
      } else {
        setPaymentMethod('cash');
        setInstallments(1);
        setFirstDueDate('');
        setIntervalDays(30);
        setInstallmentsPreview(null);
      }
    }
  }, [showPaymentModal, requisition]);

  // Inicializar dados do modal de fornecedor quando abrir
  useEffect(() => {
    if (showSupplierModal && requisition) {
      if (requisition.supplier_id) {
        const cnpjFormatted = requisition.supplier_cpf_cnpj ? formatCnpj(requisition.supplier_cpf_cnpj) : '';
        setSupplierData({
          name: requisition.supplier_name || '',
          cnpj: cnpjFormatted
        });
        setFoundSupplier({
          id: requisition.supplier_id,
          name: requisition.supplier_name,
          cpf_cnpj: requisition.supplier_cpf_cnpj
        });
      } else {
        setSupplierData({ name: '', cnpj: '' });
        setFoundSupplier(null);
      }
    }
  }, [showSupplierModal, requisition]);
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Função para carregar dados da requisição
  const loadRequisition = React.useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await mirrorService.getById(id);
      if (response.success) {
        setRequisition(response.data);
        // Verificar se já tem arquivo anexado
        if (response.data.mirror_file_url) {
          setHasUploaded(true);
          setUploadedFile({ name: response.data.mirror_file_name || 'arquivo.pdf' });
        }
        // Verificar se já tem resultados de verificação
        if (response.data.verification_status === 'verified' && response.data.extracted_items) {
          setShowResults(true);
          setExtractedItems(response.data.extracted_items);
        }
      } else {
        setError(response.message || 'Erro ao carregar requisição');
        showToast(response.message || 'Erro ao carregar requisição', 'error');
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar requisição');
      showToast(err.message || 'Erro ao carregar requisição', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  // Carregar dados da requisição
  useEffect(() => {
    loadRequisition();
  }, [loadRequisition]);

  // Scroll automático durante drag do lado esquerdo
  useEffect(() => {
    if (!draggingItem || draggingItem.type !== 'original') {
      // Limpar intervalo se existir
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    const handleMouseMove = (e) => {
      if (!leftPanelContentRef.current || !rightPanelContentRef.current) {
        // Limpar intervalo se os refs não estiverem disponíveis
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
        return;
      }

      const leftPanel = leftPanelContentRef.current;
      const rightPanel = rightPanelContentRef.current;
      const leftRect = leftPanel.getBoundingClientRect();
      const rightRect = rightPanel.getBoundingClientRect();
      
      const scrollThreshold = 80; // Distância em pixels da borda para ativar scroll
      const scrollSpeed = 8; // Velocidade do scroll
      
      const mouseY = e.clientY;
      
      // Limpar intervalo anterior
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      
      // Verificar se o mouse está próximo das bordas do painel esquerdo
      const leftDistanceFromTop = mouseY - leftRect.top;
      const leftDistanceFromBottom = leftRect.bottom - mouseY;
      
      // Verificar se o mouse está próximo das bordas do painel direito
      const rightDistanceFromTop = mouseY - rightRect.top;
      const rightDistanceFromBottom = rightRect.bottom - mouseY;
      
      // Determinar qual painel fazer scroll e em qual direção
      let targetPanel = null;
      let scrollDirection = 0; // -1 para cima, 1 para baixo
      
      // Priorizar painel esquerdo se o mouse estiver sobre ele
      if (mouseY >= leftRect.top && mouseY <= leftRect.bottom) {
        if (leftDistanceFromTop < scrollThreshold && leftDistanceFromTop > 0) {
          targetPanel = leftPanel;
          scrollDirection = -1;
        } else if (leftDistanceFromBottom < scrollThreshold && leftDistanceFromBottom > 0) {
          targetPanel = leftPanel;
          scrollDirection = 1;
        }
      }
      
      // Se não há scroll no painel esquerdo, verificar o direito
      if (!targetPanel && mouseY >= rightRect.top && mouseY <= rightRect.bottom) {
        if (rightDistanceFromTop < scrollThreshold && rightDistanceFromTop > 0) {
          targetPanel = rightPanel;
          scrollDirection = -1;
        } else if (rightDistanceFromBottom < scrollThreshold && rightDistanceFromBottom > 0) {
          targetPanel = rightPanel;
          scrollDirection = 1;
        }
      }
      
      // Criar intervalo de scroll se necessário
      if (targetPanel && scrollDirection !== 0) {
        scrollIntervalRef.current = setInterval(() => {
          if (targetPanel) {
            if (scrollDirection === -1) {
              // Scroll para cima
              targetPanel.scrollTop = Math.max(0, targetPanel.scrollTop - scrollSpeed);
            } else {
              // Scroll para baixo
              const maxScroll = targetPanel.scrollHeight - targetPanel.clientHeight;
              targetPanel.scrollTop = Math.min(maxScroll, targetPanel.scrollTop + scrollSpeed);
            }
          }
        }, 16); // ~60fps
      }
    };

    // Adicionar listener de mousemove
    document.addEventListener('mousemove', handleMouseMove);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [draggingItem]);

  // Handlers para drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      await processFile(file);
    }
  };

  // Processar arquivo (usado tanto por drag and drop quanto por input)
  const processFile = async (file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = [
      'application/pdf', 
      'application/xml', 
      'text/xml',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    const allowedExtensions = ['.pdf', '.xml', '.xlsx', '.xls'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      showToast('Apenas arquivos PDF, XML ou Excel (.xlsx, .xls) são permitidos', 'error');
      return;
    }

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('Arquivo muito grande. Tamanho máximo: 10MB', 'error');
      return;
    }

    try {
      setUploading(true);
      const response = await mirrorService.upload(id, file);
      if (response.success) {
        setHasUploaded(true);
        setUploadedFile(file);
        // Salvar os itens extraídos do upload para usar na verificação
        if (response.data?.items && Array.isArray(response.data.items)) {
          setInvoiceItems(response.data.items);
        }
        // Salvar totalWithTaxes se disponível
        if (response.data?.totalWithTaxes) {
          setTotalWithTaxes(parseFloat(response.data.totalWithTaxes));
        }
        showToast('Arquivo enviado com sucesso', 'success');
      } else {
        showToast(response.message || 'Erro ao enviar arquivo', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Erro ao enviar arquivo', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Upload de arquivo (via input)
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    await processFile(file);
  };

  // Executar match fiscal
  const handleStartAnalysis = async () => {
    // Verificar se temos invoiceItems para enviar
    if (!invoiceItems || invoiceItems.length === 0) {
      showToast('Nenhum item extraído do arquivo. Faça o upload novamente.', 'error');
      return;
    }

    try {
      setIsVerifying(true);
      // Enviar invoiceItems e totalWithTaxes
      const verifyPayload = {
        invoiceItems,
        ...(totalWithTaxes && { totalWithTaxes })
      };
      
      const response = await mirrorService.verify(id, verifyPayload);
      if (response.success) {
        // Sempre mostrar os itens extraídos, mesmo se houver erro no matching
        const extracted = response.data.extracted_items || response.data.items || invoiceItems;
        setExtractedItems(extracted);
        
        // Preservar totalWithTaxes se disponível na resposta
        if (response.data.totalWithTaxes) {
          setTotalWithTaxes(parseFloat(response.data.totalWithTaxes));
        }
        
        // Salvar resultados do matching se disponíveis
        const matches = response.data.matches || [];
        if (matches.length > 0 || response.data.summary) {
          setMatchResults({
            matches,
            summary: response.data.summary || null
          });
        }
        
        setShowResults(true);
        
        // Recarregar requisição para obter o valor total atualizado do espelho
        await loadRequisition();
        
        // Calcular ordem otimizada após um delay para garantir que os refs estão atualizados
        if (matches.length > 0) {
          setTimeout(() => {
            if (requisition?.items) {
              const optimizedOrder = calculateOptimalOrder(matches, requisition.items, extracted);
              setItemOrder(optimizedOrder);
            }
          }, 500);
        }
        
        if (response.data.error) {
          showToast('Match fiscal concluído com matching manual (dados extraídos disponíveis)', 'warning');
        } else {
          showToast('Match fiscal concluído com sucesso', 'success');
        }
      } else {
        // Mesmo em caso de erro, tentar mostrar os dados extraídos se disponíveis
        if (response.data?.extracted_items) {
          setExtractedItems(response.data.extracted_items);
          setShowResults(true);
        }
        showToast(response.message || 'Erro ao executar match fiscal', 'error');
      }
    } catch (err) {
      // Em caso de erro, ainda mostrar os itens extraídos se disponíveis
      if (invoiceItems && invoiceItems.length > 0) {
        setExtractedItems(invoiceItems);
        setShowResults(true);
        showToast('Erro no matching, mas dados extraídos disponíveis para revisão manual', 'warning');
      } else {
        showToast(err.message || 'Erro ao executar match fiscal', 'error');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Verificar se há matches
  const hasMatches = () => {
    if (!matchResults || !matchResults.matches) return false;
    return matchResults.matches.some(m => m.matchType === 'perfect' || m.matchType === 'partial');
  };

  // Encontrar conexão manual ou automática para um item extraído
  const findConnectionForExtracted = (extractedIdx) => {
    // Priorizar conexão manual
    if (manualConnections[extractedIdx]) {
      return manualConnections[extractedIdx];
    }
    // Fallback para match automático
    const match = matchResults?.matches?.[extractedIdx];
    return match?.requisitionItemId || null;
  };

  // Aprovar match
  const handleApprove = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Match Fiscal',
      message: 'Deseja realmente aprovar este match fiscal e liberar para o financeiro? O fornecedor poderá ser cadastrado ou vinculado na página de aprovação financeira.',
      type: 'success',
      onConfirm: async () => {
        try {
          const supplierId = requisition?.supplier_id || requisition?.supplierId;
          const response = await mirrorService.approve(id, supplierId);
          if (response.success) {
            showToast('Match fiscal aprovado com sucesso', 'success');
            navigate('/erp/purchases');
          } else {
            showToast(response.message || 'Erro ao aprovar match', 'error');
          }
        } catch (err) {
          showToast('Erro ao aprovar match', 'error');
        } finally {
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      }
    });
  };


  // Rejeitar match
  const handleReject = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Rejeitar Match Fiscal',
      message: 'Deseja realmente rejeitar este match fiscal? A requisição retornará para correção.',
      type: 'warning',
      onConfirm: async () => {
        try {
          // TODO: Implementar rejeição via API
          showToast('Match fiscal rejeitado', 'success');
          navigate('/erp/purchases/mirrors');
        } catch (err) {
          showToast('Erro ao rejeitar match', 'error');
        } finally {
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      }
    });
  };

  // Formatar moeda
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calcular valor total dos itens extraídos
  const calculateExtractedTotal = () => {
    if (!extractedItems || extractedItems.length === 0) {
      return 0;
    }
    return extractedItems.reduce((sum, item) => {
      // Priorizar totalPrice (que geralmente já inclui impostos)
      // Se não tiver totalPrice, calcular a partir de qty * unitPrice
      const totalPrice = parseFloat(item.totalPrice || item.total_price || 0);
      if (totalPrice > 0) {
        return sum + totalPrice;
      }
      // Fallback: calcular a partir de quantidade e preço unitário
      const qty = parseFloat(item.qty || item.quantity || 0);
      const unitPrice = parseFloat(item.unitPrice || item.price || item.unit_price || 0);
      return sum + (qty * unitPrice);
    }, 0);
  };

  // Obter valor total a ser usado (prioriza totalWithTaxes, depois calculado dos itens extraídos, depois invoice_total, depois total_value)
  const getTotalValue = () => {
    // Priorizar totalWithTaxes se disponível (valor total com impostos extraído do documento)
    if (totalWithTaxes && totalWithTaxes > 0) {
      return totalWithTaxes;
    }
    // Se temos itens extraídos, sempre usar o valor calculado (mesmo que showResults seja false)
    if (extractedItems && extractedItems.length > 0) {
      const extractedTotal = calculateExtractedTotal();
      if (extractedTotal > 0) {
        return extractedTotal;
      }
    }
    // Se temos invoice_total na requisição (valor atualizado pelo backend após verificação), usar ele
    if (requisition?.invoice_total && requisition.invoice_total > 0) {
      return requisition.invoice_total;
    }
    // Caso contrário, usar o valor da requisição original
    return requisition?.total_value || 0;
  };

  // Encontrar item da requisição correspondente usando os resultados do matching
  const findMatchedRequisitionItem = (extractedItemIndex) => {
    if (!originalItems) return null;
    
    // Priorizar conexão manual
    const manualConnectionId = manualConnections[extractedItemIndex];
    if (manualConnectionId) {
      const manualItem = originalItems.find(item => item.id === manualConnectionId);
      if (manualItem) return manualItem;
    }
    
    // Fallback para match automático
    if (!matchResults || !matchResults.matches) return null;
    
    const match = matchResults.matches[extractedItemIndex];
    if (!match || !match.requisitionItemId) return null;
    
    // Encontrar o item da requisição pelo ID
    return originalItems.find(item => item.id === match.requisitionItemId) || null;
  };

  // Comparar itens e identificar divergências
  const compareItems = (originalItem, extractedItem) => {
    if (!originalItem || !extractedItem) return null;
    
    // Usar product_cost_price (custo atual do sistema) para comparação
    // Se não tiver cost_price, tentar unit_price como fallback
    const originalPrice = originalItem.product_cost_price || 
                          originalItem.cost_price || 
                          originalItem.unit_price || 
                          originalItem.unitPrice || 
                          0;
    
    const extractedPrice = parseFloat(extractedItem.unitPrice || extractedItem.price || 0);
    const priceDiff = extractedPrice - originalPrice;
    const qtyMatch = Math.abs((extractedItem.qty || 0) - (originalItem.requested_qty || 0)) < 0.01;
    const priceMatch = Math.abs(priceDiff) < 0.01; // Tolerância de 1 centavo
    
    return {
      isDivergent: !priceMatch || !qtyMatch,
      priceDiff,
      qtyMatch,
      priceMatch,
      originalPrice,
      extractedPrice
    };
  };

  // Calcular ordem otimizada para evitar sobreposição de linhas
  const calculateOptimalOrder = (matches, originalItems, extractedItems) => {
    if (!matches || matches.length === 0 || !originalItems || !extractedItems) {
      return { original: [], extracted: [] };
    }

    // Criar mapa de matches: requisitionItemId -> [extractedItemIndexes]
    const matchMap = {};
    matches.forEach((match, extractedIdx) => {
      if (match && match.requisitionItemId) {
        if (!matchMap[match.requisitionItemId]) {
          matchMap[match.requisitionItemId] = [];
        }
        matchMap[match.requisitionItemId].push(extractedIdx);
      }
    });

    // Ordenar itens originais que têm matches primeiro
    const originalWithMatches = originalItems
      .map((item, idx) => ({ item, idx, hasMatch: !!matchMap[item.id] }))
      .sort((a, b) => {
        if (a.hasMatch && !b.hasMatch) return -1;
        if (!a.hasMatch && b.hasMatch) return 1;
        return a.idx - b.idx;
      });

    // Ordenar itens extraídos baseado na ordem dos matches
    const extractedOrder = [];
    const usedExtracted = new Set();
    
    originalWithMatches.forEach(({ item }) => {
      if (matchMap[item.id]) {
        matchMap[item.id].forEach(extractedIdx => {
          if (!usedExtracted.has(extractedIdx)) {
            extractedOrder.push(extractedIdx);
            usedExtracted.add(extractedIdx);
          }
        });
      }
    });

    // Adicionar itens extraídos sem match no final
    extractedItems.forEach((_, idx) => {
      if (!usedExtracted.has(idx)) {
        extractedOrder.push(idx);
      }
    });

    return {
      original: originalWithMatches.map(({ idx }) => idx),
      extracted: extractedOrder
    };
  };

  // Formatar CNPJ/CPF
  const formatCnpj = (value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length <= 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').replace(/(\d{3})(\d{3})(\d)/, '$1.$2.$3').replace(/(\d{3})(\d)/, '$1-$2');
    } else {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3').replace(/(\d{2})(\d{3})/, '$1.$2');
    }
  };

  // Buscar fornecedor por CNPJ
  const handleCnpjChange = async (cnpj) => {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    const formatted = formatCnpj(cnpj);
    setSupplierData({ ...supplierData, cnpj: formatted });

    if (cleanCnpj.length >= 11) {
      setSearchingSupplier(true);
      setFoundSupplier(null);
      
      try {
        const response = await supplierService.findByCnpj(cleanCnpj);
        if (response.success && response.data) {
          setFoundSupplier(response.data);
          setSupplierData({
            name: response.data.name,
            cnpj: formatted
          });
          showToast('Fornecedor encontrado e vinculado automaticamente', 'success');
        } else {
          setFoundSupplier(null);
        }
      } catch (err) {
        console.error('Erro ao buscar fornecedor:', err);
        setFoundSupplier(null);
      } finally {
        setSearchingSupplier(false);
      }
    } else {
      setFoundSupplier(null);
    }
  };

  // Calcular preview de parcelas
  const handleCalculateInstallments = async () => {
    if (paymentMethod !== 'installments' || !firstDueDate) {
      setInstallmentsPreview(null);
      return;
    }

    setCalculating(true);
    setPaymentErrors({});

    try {
      const response = await financeService.calculateInstallments(id, {
        installments,
        firstDueDate,
        intervalDays
      });

      if (response.success) {
        setInstallmentsPreview(response.data);
      } else {
        setPaymentErrors({ general: response.message || 'Erro ao calcular parcelas' });
      }
    } catch (error) {
      console.error('Erro ao calcular parcelas:', error);
      setPaymentErrors({ general: 'Erro ao calcular parcelas' });
    } finally {
      setCalculating(false);
    }
  };

  // Calcular automaticamente quando mudar dados relevantes
  useEffect(() => {
    if (showPaymentModal && paymentMethod === 'installments' && firstDueDate && installments > 0) {
      const timeoutId = setTimeout(() => {
        handleCalculateInstallments();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, paymentMethod, installments, firstDueDate, intervalDays]);

  // Salvar forma de pagamento
  const handleSavePaymentMethod = async () => {
    const newErrors = {};

    if (paymentMethod === 'installments') {
      if (!installments || installments < 1 || installments > 12) {
        newErrors.installments = 'Número de parcelas deve estar entre 1 e 12';
      }
      if (!firstDueDate) {
        newErrors.firstDueDate = 'Data do primeiro vencimento é obrigatória';
      }
      if (!intervalDays || intervalDays < 7 || intervalDays > 90) {
        newErrors.intervalDays = 'Intervalo deve estar entre 7 e 90 dias';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setPaymentErrors(newErrors);
      return;
    }

    const paymentData = {
      payment_method: paymentMethod,
      payment_installments: paymentMethod === 'installments' ? installments : null,
      payment_first_due_date: paymentMethod === 'installments' ? firstDueDate : null,
      payment_interval_days: paymentMethod === 'installments' ? intervalDays : null
    };

    try {
      const response = await financeService.updatePaymentMethod(id, paymentData);
      if (response.success) {
        showToast('Forma de pagamento atualizada com sucesso', 'success');
        await loadRequisition();
        setShowPaymentModal(false);
      } else {
        showToast(response.message || 'Erro ao atualizar forma de pagamento', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar forma de pagamento:', error);
      showToast('Erro ao atualizar forma de pagamento', 'error');
    }
  };

  // Salvar fornecedor
  const handleSaveSupplier = async () => {
    if (!supplierData.name) {
      showToast('Nome do fornecedor é obrigatório', 'error');
      return;
    }

    try {
      let finalSupplierId = foundSupplier?.id;
      
      if (!finalSupplierId) {
        const createResponse = await supplierService.create({
          name: supplierData.name,
          cpfCnpj: supplierData.cnpj.replace(/\D/g, '')
        });
        if (createResponse.success && createResponse.data?.id) {
          finalSupplierId = createResponse.data.id;
        } else {
          showToast(createResponse.message || 'Erro ao criar fornecedor', 'error');
          return;
        }
      }

      // Validar que temos um ID válido antes de atualizar
      if (!finalSupplierId) {
        showToast('Erro: ID do fornecedor não encontrado', 'error');
        return;
      }

      const updateResponse = await purchaseService.update(id, {
        supplier_id: finalSupplierId
      });

      if (updateResponse.success) {
        showToast(
          foundSupplier 
            ? 'Fornecedor vinculado com sucesso' 
            : 'Fornecedor cadastrado e vinculado com sucesso',
          'success'
        );
        await loadRequisition();
        setShowSupplierModal(false);
      } else {
        showToast(updateResponse.message || 'Erro ao vincular fornecedor', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar fornecedor:', error);
      showToast(error.message || 'Erro ao atualizar fornecedor', 'error');
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Se for string no formato YYYY-MM-DD, criar data local (não UTC)
    // Isso evita problemas de timezone que podem subtrair um dia
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Para outros formatos, usar parse normal
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Obter data mínima (hoje)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="mirror-verification-page">
        <div className="mirror-verification-loading">
          <Loader2 className="mirror-verification-spinner" size={48} />
          <p>Carregando requisição...</p>
        </div>
      </div>
    );
  }

  if (error || !requisition) {
    return (
      <div className="mirror-verification-page">
        <div className="mirror-verification-error">
          <AlertCircle size={48} />
          <p>{error || 'Requisição não encontrada'}</p>
          <button 
            onClick={() => navigate('/erp/purchases/mirrors')} 
            className="mirror-verification-back-button"
          >
            Voltar para Lista
          </button>
        </div>
      </div>
    );
  }

  const originalItems = requisition.items || [];

  return (
    <div className="mirror-verification-page">
      {/* Toolbar */}
      <div className="mirror-verification-toolbar">
        <div className="mirror-verification-toolbar-left">
          <button 
            onClick={() => navigate('/erp/purchases/mirrors')} 
            className="mirror-verification-back-icon"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="mirror-verification-title-row">
              <h1 className="mirror-verification-title">
                {requisition.requisition_id || `RQ-${id}`}
              </h1>
              <span className="mirror-verification-status-badge">
                Auditando Match
              </span>
            </div>
            <p className="mirror-verification-subtitle">
              Fornecedor: {requisition.supplier_name || 'Não informado'}
            </p>
          </div>
        </div>

        <div className="mirror-verification-toolbar-right">
          <div className="mirror-verification-toolbar-actions">
            {/* Botão Fornecedor */}
            <button
              className="mirror-verification-toolbar-icon-btn"
              onClick={() => setShowSupplierModal(true)}
              title={requisition?.supplier_name ? `Fornecedor: ${requisition.supplier_name}` : 'Adicionar Fornecedor'}
            >
              <Building2 size={18} />
            </button>

            {/* Botão Forma de Pagamento */}
            <button
              className="mirror-verification-toolbar-icon-btn"
              onClick={() => setShowPaymentModal(true)}
              title={requisition?.payment_method 
                ? `Forma de Pagamento: ${requisition.payment_method === 'cash' ? 'À Vista' : `${requisition.payment_installments}x Parcelas`}` 
                : 'Definir Forma de Pagamento'}
            >
              <CreditCard size={18} />
            </button>
          </div>

          {!hasUploaded ? (
            <label className="mirror-verification-upload-button">
              <Upload size={14} />
              Anexar Mirror (PDF/XML/Excel)
              <input 
                type="file" 
                className="mirror-verification-file-input"
                accept=".pdf,.xml,.xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          ) : !showResults ? (
            <button
              onClick={handleStartAnalysis}
              disabled={isVerifying || uploading}
              className="mirror-verification-analyze-button"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mirror-verification-spinner-small" size={16} />
                  Extraindo Dados via Gemini...
                </>
              ) : (
                <>
                  <ScanSearch size={16} />
                  Executar Match Fiscal
                  <BrainCircuit size={14} style={{ marginLeft: '0.25rem' }} />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => {
                setShowResults(false);
                setHasUploaded(false);
                setExtractedItems([]);
                setInvoiceItems([]);
              }}
              className="mirror-verification-reset-button"
            >
              <RefreshCcw size={14} />
              Refazer Auditoria
            </button>
          )}
        </div>
      </div>

      {/* Split View */}
      <div className="mirror-verification-split" ref={splitContainerRef} style={{ position: 'relative' }}>
        {/* Left Side: Original Requisition */}
        <div className="mirror-verification-panel mirror-verification-panel-left">
          <div className="mirror-verification-panel-header">
            <h3 className="mirror-verification-panel-title">
              <FileCheck size={16} />
              Requisição Original (PO)
            </h3>
            <span className="mirror-verification-panel-badge">Dados Originais</span>
          </div>

          <div className="mirror-verification-panel-content" ref={leftPanelContentRef}>
            {originalItems.length === 0 ? (
              <div className="mirror-verification-empty">
                <FileText size={48} />
                <p>Nenhum item encontrado</p>
              </div>
            ) : (
              (itemOrder.original.length > 0 ? itemOrder.original : originalItems.map((_, idx) => idx)).map((orderIdx, displayIdx) => {
                const item = originalItems[orderIdx];
                const idx = orderIdx;
                return (
                <div 
                  key={item.id || idx} 
                  className={`mirror-verification-item-card ${draggingItem?.type === 'original' && draggingItem?.id === (item.id || idx) ? 'mirror-verification-item-dragging' : ''} ${hoveringItem?.type === 'original' && hoveringItem?.id === (item.id || idx) ? 'mirror-verification-item-hovering' : ''}`}
                  ref={el => {
                    if (el) originalItemRefs.current[item.id || idx] = el;
                  }}
                  draggable={true}
                  onDragStart={(e) => {
                    setDraggingItem({ type: 'original', index: orderIdx, id: item.id || idx });
                    e.dataTransfer.effectAllowed = 'link';
                    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'original', id: item.id || idx }));
                  }}
                  onDragEnd={() => {
                    setDraggingItem(null);
                    setHoveringItem(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'link';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (data.type === 'extracted') {
                      // Conectar item extraído a este item original
                      setManualConnections(prev => ({
                        ...prev,
                        [data.id]: item.id || idx
                      }));
                      showToast(`Item conectado manualmente`, 'success');
                    }
                    setDraggingItem(null);
                    setHoveringItem(null);
                  }}
                >
                  <div className="mirror-verification-item-header">
                    <div>
                      <p className="mirror-verification-item-sku">
                        {item.product_sku || item.sku || 'N/A'}
                      </p>
                      <h4 className="mirror-verification-item-name">
                        {item.product_name || item.name || 'Produto sem nome'}
                      </h4>
                    </div>
                    <span className="mirror-verification-item-index">Item {idx + 1}</span>
                  </div>
                  <div className="mirror-verification-item-details">
                    <div>
                      <p className="mirror-verification-item-label">Qtd Acordada</p>
                      <p className="mirror-verification-item-value">
                        {item.requested_qty || item.qty || 0} un
                      </p>
                    </div>
                    <div className="mirror-verification-item-detail-right">
                      <p className="mirror-verification-item-label">P.U. Acordado</p>
                      <p className="mirror-verification-item-value">
                        {formatCurrency(
                          item.product_cost_price || item.cost_price || item.unit_price || item.unitPrice || 0
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>

          <div className="mirror-verification-panel-footer">
            <p className="mirror-verification-footer-label">Total Líquido</p>
            <p className="mirror-verification-footer-value">
              {formatCurrency(
                originalItems.reduce((acc, item) => {
                  const qty = item.requested_qty || item.qty || 0;
                  const price = item.product_cost_price || item.cost_price || item.unit_price || item.unitPrice || 0;
                  return acc + (qty * price);
                }, 0)
              )}
            </p>
          </div>
        </div>

        {/* Center Divider */}
        <div className="mirror-verification-divider">
          <div className="mirror-verification-divider-line" />
          <div className="mirror-verification-divider-icon">
            <BrainCircuit size={20} />
          </div>
          <div className="mirror-verification-divider-line" />
        </div>
        
        {/* Conectores visuais - renderizados no container split */}
        {showResults && extractedItems.length > 0 && (
          <div className="mirror-verification-connectors">
            {extractedItems.map((_, extractedIdx) => {
              // Usar conexão manual ou automática
              const originalItemId = findConnectionForExtracted(extractedIdx);
              if (!originalItemId) return null;
              
              const originalRef = originalItemRefs.current[originalItemId];
              const extractedRef = extractedItemRefs.current[extractedIdx];
              
              if (!originalRef || !extractedRef) return null;
              
              // Determinar tipo de match para cor
              const match = matchResults?.matches?.[extractedIdx];
              const isManualConnection = !!manualConnections[extractedIdx];
              
              // Determinar cor baseada no tipo de match
              let connectorColor = '#6366f1'; // padrão
              let isActive = true;
              
              if (isManualConnection) {
                connectorColor = '#8b5cf6'; // roxo para conexões manuais
                isActive = true;
              } else if (match) {
                if (match.matchType === 'perfect') {
                  connectorColor = '#22c55e'; // verde
                } else if (match.matchType === 'divergent' || match.matchType === 'not_found') {
                  connectorColor = '#f43f5e'; // vermelho
                  isActive = false;
                } else if (match.matchType === 'partial') {
                  connectorColor = '#f59e0b'; // amarelo/laranja
                }
              }
              
              return (
                <ItemConnector
                  key={`connector-${originalItemId}-${extractedIdx}`}
                  fromRef={{ current: originalRef }}
                  toRef={{ current: extractedRef }}
                  color={connectorColor}
                  strokeWidth={isManualConnection ? 3 : 2}
                  isActive={isActive}
                />
              );
            })}
          </div>
        )}

        {/* Right Side: Gemini Extraction Results */}
        <div className="mirror-verification-panel mirror-verification-panel-right">
          {!hasUploaded && !uploading ? (
            <div 
              className={`mirror-verification-empty-state ${isDragging ? 'mirror-verification-drag-over' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: isDragging ? '2px dashed var(--primary)' : '2px dashed transparent',
                background: isDragging ? 'rgba(34, 197, 94, 0.05)' : 'transparent',
                borderRadius: '1rem',
                padding: isDragging ? '2rem' : '0'
              }}
            >
              <div className="mirror-verification-empty-icon">
                <Upload size={48} />
              </div>
              <h3 className="mirror-verification-empty-title">
                {isDragging ? 'Solte o arquivo aqui' : 'Fila Fiscal Vazia'}
              </h3>
              <p className="mirror-verification-empty-text">
                {isDragging 
                  ? 'Solte o arquivo PDF, XML ou Excel para iniciar a análise'
                  : 'Arraste e solte ou clique para anexar o espelho enviado pelo fornecedor para que o Gemini realize a auditoria automática de preços.'}
              </p>
              <input 
                type="file" 
                className="mirror-verification-file-input"
                accept=".pdf,.xml,.xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                style={{ display: 'none' }}
                id="mirror-file-input"
              />
              <label 
                htmlFor="mirror-file-input"
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  background: 'var(--primary)',
                  color: '#fff',
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  display: 'inline-block',
                  transition: 'all 0.2s'
                }}
              >
                Selecionar Arquivo
              </label>
            </div>
          ) : uploading ? (
            <div className="mirror-verification-loading-state">
              <Loader2 className="mirror-verification-spinner-large" size={64} />
              <h3 className="mirror-verification-loading-title">Extraindo Dados...</h3>
              <p className="mirror-verification-loading-text">
                Processando arquivo e extraindo informações
              </p>
            </div>
          ) : isVerifying ? (
            <div className="mirror-verification-loading-state">
              <Loader2 className="mirror-verification-spinner-large" size={64} />
              <h3 className="mirror-verification-loading-title">Extraindo OCR...</h3>
              <p className="mirror-verification-loading-text">
                Mapeando CFOP e Bases de Cálculo
              </p>
            </div>
          ) : showResults ? (
            <>
              <div className="mirror-verification-panel-header mirror-verification-panel-header-success">
                <h3 className="mirror-verification-panel-title">
                  <Sparkles size={16} />
                  Extração Gemini AI
                </h3>
                <span className="mirror-verification-panel-badge-success">Match Concluído</span>
              </div>

              <div className="mirror-verification-panel-content" ref={rightPanelContentRef}>
                {extractedItems.length === 0 ? (
                  <div className="mirror-verification-empty">
                    <FileText size={48} />
                    <p>Nenhum item extraído</p>
                  </div>
                ) : (
                  (itemOrder.extracted.length > 0 ? itemOrder.extracted : extractedItems.map((_, idx) => idx)).map((orderIdx, displayIdx) => {
                    const extractedItem = extractedItems[orderIdx];
                    const idx = orderIdx;
                    // Usar resultados do matching para encontrar o item correspondente da requisição
                    const matchedRequisitionItem = findMatchedRequisitionItem(idx);
                    const match = matchResults?.matches?.[idx];
                    const hasManualConnection = !!manualConnections[idx];
                    
                    // Se não encontrou match, mostrar como não encontrado
                    const originalItem = matchedRequisitionItem;
                    const comparison = originalItem ? compareItems(originalItem, extractedItem) : null;
                    
                    // Determinar estado do match
                    // Se há conexão manual, considerar como match válido
                    const hasMatch = hasManualConnection || (match && match.matchType !== 'not_found');
                    const isPerfectMatch = hasManualConnection ? false : (match && match.matchType === 'perfect');
                    const hasQtyDifference = comparison && !comparison.qtyMatch;
                    const hasPriceDifference = comparison && !comparison.priceMatch;
                    // Só é divergente se não há match automático E não há conexão manual
                    // Diferenças de quantidade/preço com match válido não são consideradas divergentes
                    const isDivergent = !hasManualConnection && (!match || match.matchType === 'not_found' || match.matchType === 'divergent');
                    
                    // Determinar classe CSS baseado no estado
                    let cardClassName = 'mirror-verification-item-card';
                    if (isDivergent) {
                      cardClassName += ' mirror-verification-item-divergent';
                    } else if (hasMatch) {
                      if (hasManualConnection) {
                        // Conexão manual - usar estilo de match sucesso (verde)
                        cardClassName += ' mirror-verification-item-match-success';
                      } else if (isPerfectMatch && !hasQtyDifference && !hasPriceDifference) {
                        cardClassName += ' mirror-verification-item-match-perfect';
                      } else if (hasQtyDifference) {
                        cardClassName += ' mirror-verification-item-match-qty-diff';
                      } else {
                        cardClassName += ' mirror-verification-item-match-success';
                      }
                    }
                    
                    return (
                      <div 
                        key={idx} 
                        className={`${cardClassName} ${draggingItem?.type === 'extracted' && draggingItem?.index === idx ? 'mirror-verification-item-dragging' : ''} ${hoveringItem?.type === 'extracted' && hoveringItem?.index === idx ? 'mirror-verification-item-hovering' : ''}`}
                        ref={el => {
                          if (el) extractedItemRefs.current[idx] = el;
                        }}
                        draggable={true}
                        onDragStart={(e) => {
                          setDraggingItem({ type: 'extracted', index: idx, id: idx });
                          e.dataTransfer.effectAllowed = 'link';
                          e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'extracted', id: idx }));
                        }}
                        onDragEnd={() => {
                          setDraggingItem(null);
                          setHoveringItem(null);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'link';
                          setHoveringItem({ type: 'extracted', index: idx, id: idx });
                        }}
                        onDragLeave={() => {
                          setHoveringItem(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                          if (data.type === 'original') {
                            // Conectar este item extraído ao item original
                            setManualConnections(prev => ({
                              ...prev,
                              [idx]: data.id
                            }));
                            showToast(`Item conectado manualmente`, 'success');
                          }
                          setDraggingItem(null);
                          setHoveringItem(null);
                        }}
                      >
                        <div className="mirror-verification-item-header">
                          <div>
                            <p className={`mirror-verification-item-sku ${isDivergent ? 'mirror-verification-item-sku-divergent' : 'mirror-verification-item-sku-match'}`}>
                              {extractedItem.sku || 'N/A'}
                            </p>
                            <h4 className="mirror-verification-item-name">
                              {extractedItem.name || 'Produto sem nome'}
                            </h4>
                          </div>
                          {isDivergent ? (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <AlertCircle className="mirror-verification-item-alert" size={20} />
                              {hasMatches() && matchResults?.matches?.[idx]?.matchType === 'partial' && (
                                <RefreshCcw 
                                  size={14} 
                                  style={{ 
                                    position: 'absolute', 
                                    top: -4, 
                                    right: -4, 
                                    background: '#fff', 
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                  }}
                                  onClick={() => {
                                    // Atualizar item com dados do match
                                    const match = matchResults.matches[idx];
                                    if (match && match.requisitionItemId) {
                                      showToast('Funcionalidade de atualização em desenvolvimento', 'info');
                                    }
                                  }}
                                />
                              )}
                            </div>
                          ) : hasManualConnection ? (
                            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                              <CheckCircle2 className="mirror-verification-item-check" size={20} style={{ color: '#8b5cf6' }} />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setManualConnections(prev => {
                                    const newConnections = { ...prev };
                                    delete newConnections[idx];
                                    return newConnections;
                                  });
                                  showToast('Conexão removida', 'info');
                                }}
                                className="mirror-verification-unlink-button"
                                title="Desvincular conexão manual"
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          ) : hasQtyDifference ? (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <AlertCircle className="mirror-verification-item-alert" size={20} style={{ color: '#f59e0b' }} />
                            </div>
                          ) : (
                            <CheckCircle2 className="mirror-verification-item-check" size={20} style={{ color: '#22c55e' }} />
                          )}
                        </div>
                        <div className="mirror-verification-item-details">
                          <div>
                            <p className="mirror-verification-item-label">Qtd no Documento</p>
                            <p className="mirror-verification-item-value">
                              {extractedItem.qty || 0} un
                            </p>
                          </div>
                          <div className="mirror-verification-item-detail-right">
                            <p className={`mirror-verification-item-label ${isDivergent ? 'mirror-verification-item-label-divergent' : ''}`}>
                              P.U. Extraído
                            </p>
                            <p className={`mirror-verification-item-value ${isDivergent ? 'mirror-verification-item-value-divergent' : ''}`}>
                              {formatCurrency(extractedItem.unitPrice || 0)}
                            </p>
                          </div>
                        </div>
                        {(isDivergent || hasQtyDifference || (comparison && comparison.priceDiff !== undefined && Math.abs(comparison.priceDiff) > 0.01)) && (
                          <div className={`mirror-verification-item-warning ${hasQtyDifference ? 'mirror-verification-item-warning-qty' : ''}`}>
                            <Info size={14} />
                            <p>
                              {!hasManualConnection && (!match || match.matchType === 'not_found') ? (
                                <>Item não encontrado na requisição original.</>
                              ) : hasManualConnection ? (
                                <>Conexão manual estabelecida. {hasQtyDifference && comparison ? <>Diferença de quantidade: <strong>{Math.abs((extractedItem.qty || 0) - (originalItem?.requested_qty || 0))}</strong> unidade(s).</> : ''} {comparison && comparison.priceDiff !== undefined && Math.abs(comparison.priceDiff) > 0.01 ? <>Variação de preço: <strong>{formatCurrency(Math.abs(comparison.priceDiff))}</strong> p/unidade.</> : ''}</>
                              ) : hasQtyDifference && comparison ? (
                                <>Diferença de quantidade: <strong>{Math.abs((extractedItem.qty || 0) - (originalItem?.requested_qty || 0))}</strong> unidade(s). {comparison.priceDiff !== undefined && Math.abs(comparison.priceDiff) > 0.01 ? `Variação de preço: ${formatCurrency(Math.abs(comparison.priceDiff))} p/unidade.` : ''}</>
                              ) : comparison && comparison.priceDiff !== undefined && Math.abs(comparison.priceDiff) > 0.01 ? (
                                <>Variação de <strong>{formatCurrency(Math.abs(comparison.priceDiff))}</strong> p/unidade em relação ao custo atual.</>
                              ) : (
                                <>Divergência detectada. Verifique quantidade e preço.</>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="mirror-verification-panel-footer mirror-verification-panel-footer-actions">
                <button 
                  onClick={handleReject}
                  className="mirror-verification-reject-button"
                >
                  Recusar Mirror
                </button>
                <button
                  onClick={handleApprove}
                  className="mirror-verification-approve-button"
                >
                  Liberar para Financeiro
                  <ArrowRight size={16} />
                </button>
              </div>
            </>
          ) : (
            <div className="mirror-verification-uploaded-state">
              <div className="mirror-verification-uploaded-icon">
                <FileText size={32} />
              </div>
              <h3 className="mirror-verification-uploaded-title">Documento Anexado</h3>
              <p className="mirror-verification-uploaded-text">
                {uploadedFile?.name || 'Arquivo anexado com sucesso'}
              </p>
              <p className="mirror-verification-uploaded-subtext">
                Aguardando gatilho de auditoria para processar o match com o Gemini.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      {/* Payment Method Modal - Estilo Prospecting */}
      {showPaymentModal && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => setShowPaymentModal(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '36rem' }}>
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper">
                  <CreditCard size={24} />
                </div>
                <div>
                  <h3 className="prospecting-modal-title">Forma de Pagamento</h3>
                  <p className="prospecting-modal-subtitle">Definir Condições de Pagamento</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setShowPaymentModal(false)}
                className="prospecting-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="prospecting-modal-body">
              {paymentErrors.general && (
                <div style={{ 
                  padding: '1rem', 
                  marginBottom: '1.5rem', 
                  background: 'rgba(239, 68, 68, 0.1)', 
                  color: '#ef4444',
                  borderRadius: '1rem',
                  fontSize: '0.75rem'
                }}>
                  {paymentErrors.general}
                </div>
              )}

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">Forma de Pagamento *</label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{
                    flex: 1,
                    padding: '1rem',
                    background: paymentMethod === 'cash' ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-secondary)',
                    border: `2px solid ${paymentMethod === 'cash' ? '#22c55e' : 'transparent'}`,
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setInstallmentsPreview(null);
                      }}
                      style={{ display: 'none' }}
                    />
                    <DollarSign size={20} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>À Vista</span>
                  </label>
                  <label style={{
                    flex: 1,
                    padding: '1rem',
                    background: paymentMethod === 'installments' ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-secondary)',
                    border: `2px solid ${paymentMethod === 'installments' ? '#22c55e' : 'transparent'}`,
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="installments"
                      checked={paymentMethod === 'installments'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ display: 'none' }}
                    />
                    <CreditCard size={20} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Parcelado</span>
                  </label>
                </div>
              </div>

              {paymentMethod === 'installments' && (
                <>
                  <div className="prospecting-form-grid">
                    <div className="prospecting-form-group">
                      <label className="prospecting-form-label">Número de Parcelas *</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={installments}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setInstallments(Math.min(12, Math.max(1, value)));
                        }}
                        className="prospecting-form-input"
                        style={paymentErrors.installments ? { border: '2px solid #ef4444' } : {}}
                      />
                      {paymentErrors.installments && (
                        <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                          {paymentErrors.installments}
                        </span>
                      )}
                    </div>
                    <div className="prospecting-form-group">
                      <label className="prospecting-form-label">Intervalo (dias) *</label>
                      <input
                        type="number"
                        min="7"
                        max="90"
                        value={intervalDays}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 30;
                          setIntervalDays(Math.min(90, Math.max(7, value)));
                        }}
                        className="prospecting-form-input"
                        style={paymentErrors.intervalDays ? { border: '2px solid #ef4444' } : {}}
                      />
                      {paymentErrors.intervalDays && (
                        <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                          {paymentErrors.intervalDays}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="prospecting-form-group">
                    <label className="prospecting-form-label">Primeiro Vencimento *</label>
                    <input
                      type="date"
                      min={getMinDate()}
                      value={firstDueDate}
                      onChange={(e) => setFirstDueDate(e.target.value)}
                      className="prospecting-form-input"
                      style={paymentErrors.firstDueDate ? { border: '2px solid #ef4444' } : {}}
                    />
                    {paymentErrors.firstDueDate && (
                      <span style={{ fontSize: '0.625rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        {paymentErrors.firstDueDate}
                      </span>
                    )}
                  </div>

                  {calculating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                      <Loader2 className="spinner" size={18} />
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Calculando parcelas...</span>
                    </div>
                  )}

                  {installmentsPreview && !calculating && (
                    <div style={{
                      padding: '1.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '2rem',
                      marginBottom: '1.5rem'
                    }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 900, marginBottom: '1rem' }}>Preview das Parcelas</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {installmentsPreview.installments?.map((inst, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            background: 'var(--bg-primary)',
                            borderRadius: '1rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Calendar size={14} />
                              <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                {inst.installmentNumber}ª Parcela - {formatDate(inst.dueDate)}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.875rem', fontWeight: 900 }}>
                              {formatCurrency(inst.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {paymentMethod === 'cash' && (
                <div style={{
                  padding: '1.5rem',
                  background: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <DollarSign size={24} style={{ color: '#22c55e' }} />
                  <div>
                    <p style={{ fontSize: '0.625rem', fontWeight: 900, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: 0 }}>
                      Pagamento à Vista
                    </p>
                    <p style={{ fontSize: '1.125rem', fontWeight: 900, color: 'var(--text-primary)', margin: '0.25rem 0 0 0' }}>
                      {formatCurrency(getTotalValue())}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="prospecting-modal-footer">
              <button 
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="prospecting-btn"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              >
                Cancelar
              </button>
              <button 
                type="button"
                onClick={handleSavePaymentMethod}
                className="prospecting-btn prospecting-btn-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Modal - Estilo Prospecting */}
      {showSupplierModal && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => setShowSupplierModal(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '36rem' }}>
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper">
                  <Building2 size={24} />
                </div>
                <div>
                  <h3 className="prospecting-modal-title">Fornecedor</h3>
                  <p className="prospecting-modal-subtitle">Cadastrar ou Vincular Fornecedor</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setShowSupplierModal(false)}
                className="prospecting-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="prospecting-modal-body">
              {foundSupplier && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid #10b981',
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#10b981'
                }}>
                  <CheckCircle2 size={16} />
                  <strong style={{ fontSize: '0.75rem' }}>Fornecedor encontrado:</strong>
                  <span style={{ fontSize: '0.75rem' }}>{foundSupplier.name}</span>
                </div>
              )}

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">CNPJ/CPF</label>
                <input
                  type="text"
                  value={supplierData.cnpj}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  placeholder="00.000.000/0000-00 ou 000.000.000-00"
                  className="prospecting-form-input"
                />
                {searchingSupplier && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Loader2 className="spinner" size={14} />
                    <span style={{ fontSize: '0.625rem', color: 'var(--text-secondary)' }}>
                      Buscando fornecedor...
                    </span>
                  </div>
                )}
              </div>

              <div className="prospecting-form-group">
                <label className="prospecting-form-label">Nome do Fornecedor *</label>
                <input
                  type="text"
                  value={supplierData.name}
                  onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
                  placeholder="Nome completo ou razão social"
                  required
                  disabled={!!foundSupplier}
                  className="prospecting-form-input"
                  style={foundSupplier ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>
            <div className="prospecting-modal-footer">
              <button 
                type="button"
                onClick={() => {
                  setShowSupplierModal(false);
                  setSupplierData({ name: '', cnpj: '' });
                  setFoundSupplier(null);
                }}
                className="prospecting-btn"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              >
                Cancelar
              </button>
              <button 
                type="button"
                onClick={handleSaveSupplier}
                className="prospecting-btn prospecting-btn-primary"
                disabled={!supplierData.name}
              >
                {foundSupplier ? 'Vincular' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MirrorVerificationPage;


